import React, { useState, useCallback, useEffect, useMemo } from 'react';
// @mui
import Card from '@mui/material/Card';
// import Button from '@mui/material/Button'; // Unused
// import LoadingButton from '@mui/lab/LoadingButton'; // Unused
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Unstable_Grid2';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress';
// routes
import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hook';
import { useSettingsContext } from 'src/components/settings';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';
// hooks
import { VuonTrong } from 'src/types/vuontrong';
import { LoSam } from 'src/types/losam';
import { CaySam } from 'src/types/caysam';
// local hooks & sections
import { losamApiService } from 'src/api/vuontrong-api';
import useDashboardSam from 'src/sections/overview/app/hooks/useSam';
import AppWidgetSummary from 'src/sections/overview/app/app-widget-summary';
import AppFeatured from 'src/sections/overview/app/app-featured';
// import AppWelcome from 'src/sections/overview/app/app-welcome'; // Unused
import AppCurrentDownload from 'src/sections/overview/app/app-current-download';
import AppSensorThermometer from 'src/sections/overview/app/app-sensor';
import { useTheme } from '@mui/material/styles';
import useVuonTrong from '../../vuontrong/hooks/use-vuontrong';
import { useLoSam } from '../hooks/use-losam';
import useTraCuuLoSam, { LoSamPosition } from '../hooks/use-tracu';
import CaySamDrawer from '../cay-sam/cay-sam-drawer';
import LotGrid from '../components/lot-grid';
import UpdateCaySams from '../cay-sam/update-cay-sams';
import CameraViewer from '../components/camera-viewer';
// [UPDATE] Import hook mới thay vì dữ liệu mock
import useFeaturedCaySam from '../hooks/use-featured-caysam';

// ----------------------------------------------------------------------

export default function TraCuuLoSamHomeView() {
  const theme = useTheme();
  const settings = useSettingsContext();
  const router = useRouter();

  // [UPDATE] Sử dụng Hook Featured Cay Sam
  const { featuredList, loading: featuredLoading } = useFeaturedCaySam();

  const [selectedGarden, setSelectedGarden] = useState<VuonTrong | null>(null);
  const [selectedLot, setSelectedLot] = useState<LoSam | null>(null);
  const [gridSize, setGridSize] = useState({ x: 0, y: 0 });
  const [gridPositions, setGridPositions] = useState<LoSamPosition[][]>([]);
  const [selectedPositions, setSelectedPositions] = useState<LoSamPosition[]>([]);
  const [lotName, setLotName] = useState('');
  // const [loading, setLoading] = useState(false); // Unused local loading state
  // moved to hook
  const [lots, setLots] = useState<LoSam[]>([]);
  const [lotsLoading, setLotsLoading] = useState(false);
  const [caySams, setCaySams] = useState<CaySam[]>([]);
  const [caySamsLoading, setCaySamsLoading] = useState(false);
  const [lotDetailLoading, setLotDetailLoading] = useState(false);
  const [lotDetailLoaded, setLotDetailLoaded] = useState(false);

  // State for deselected ginsengs when filtering
  const [deselectedGinsengs, setDeselectedGinsengs] = useState<Set<string>>(new Set());

  // Luôn bỏ qua cây đã có nhật ký trong tháng này
  const excludeLoggedThisMonth = true;

  // State for update growth modal
  const [updateGrowthOpen, setUpdateGrowthOpen] = useState(false);
  const [filteredCaySams, setFilteredCaySams] = useState<any[]>([]);

  const { gardens, loading: gardensLoading } = useVuonTrong();
  const { optiongardens, getOptionVuonTrong } = useVuonTrong();
  const { listLotsByVuonTrong, getLoSamById, parseViTriTrongLo, diemSucKhoeOptions } = useLoSam();

  // Load lots when garden is selected
  const loadLots = useCallback(
    async (vuonTrongId: number) => {
      try {
        setLotsLoading(true);
        const data = await listLotsByVuonTrong(vuonTrongId);
        setLots(data);
      } catch (error) {
        console.error('Error loading lots:', error);
        setLots([]);
      } finally {
        setLotsLoading(false);
      }
    },
    [listLotsByVuonTrong]
  );

  // Load cây sâm when lot is selected
  const loadCaySams = useCallback(
    async (loSamId: number) => {
      try {
        setCaySamsLoading(true);
        const response = await getLoSamById(loSamId);
        const lotData = response.oneItem as LoSam | null;
        setCaySams(lotData?.caySams || []);
      } catch (error) {
        console.error('Error loading cây sâm:', error);
        setCaySams([]);
      } finally {
        setCaySamsLoading(false);
      }
    },
    [getLoSamById]
  );

  // Reload lot detail and update grid positions
  const reloadLotDetail = useCallback(
    async (loSamId: number) => {
      try {
        const lotDetail = await losamApiService.getLoSam(loSamId);
        if (lotDetail.messCode === 1 && lotDetail.oneItem) {
          setSelectedLot(lotDetail.oneItem as LoSam);

          // Cập nhật gridPositions với dữ liệu mới
          const totalCols = lotDetail.oneItem.soCot + 1;
          const totalRows = lotDetail.oneItem.soHang;

          const positions: LoSamPosition[][] = [];
          for (let y = 0; y < totalRows; y += 1) {
            const row: LoSamPosition[] = [];
            for (let x = 0; x < totalCols; x += 1) {
              row.push({
                x,
                y,
                selected: false,
                occupied: false,
                lotId: lotDetail.oneItem.loSam_ID || 0,
              });
            }
            positions.push(row);
          }
          setGridPositions(positions);

          // Load cây sâm data
          await loadCaySams(loSamId);
        }
      } catch (error) {
        console.error('Error reloading lot detail:', error);
      }
    },
    [loadCaySams]
  );

  const handleGardenChange = useCallback(
    (gardenId: number) => {
      const garden = gardens.find((g) => g.vuonTrong_ID === gardenId);
      setSelectedGarden(garden || null);
      setSelectedLot(null);
      setGridPositions([]);
      setSelectedPositions([]);
      setGridSize({ x: 0, y: 0 });
      setLots([]);
      setCaySams([]);

      if (garden) {
        loadLots(garden.vuonTrong_ID);
      }
    },
    [gardens, loadLots]
  );

  const handleLotChange = useCallback(
    async (lotId: number) => {
      const lot = lots.find((l) => l.loSam_ID === lotId);
      setSelectedLot(lot || null);
      setLotDetailLoading(true);
      setLotDetailLoaded(false);

      // ===== RESET TẤT CẢ STATE TRƯỚC KHI LOAD MỚI =====
      setGridPositions([]);
      setCaySams([]);
      setSelectedPositions([]);
      setGridSize({ x: 0, y: 0 });
      setLotName('');

      if (lot) {
        try {
          // Gọi API để lấy thông tin chi tiết lô sâm (bao gồm loSamCameras)
          const lotDetail = await losamApiService.getLoSam(lotId);

          if (lotDetail.messCode === 1 && lotDetail.oneItem) {
            // Cập nhật selectedLot với dữ liệu chi tiết từ API
            setSelectedLot(lotDetail.oneItem as LoSam);

            // Add 1 to soCot for walkway
            const totalCols = lotDetail.oneItem.soCot + 1;
            const totalRows = lotDetail.oneItem.soHang;

            setGridSize({ x: totalCols, y: totalRows });
            setLotName(lotDetail.oneItem.tenLo);

            // Initialize EMPTY grid positions FIRST
            const positions: LoSamPosition[][] = [];
            for (let y = 0; y < totalRows; y += 1) {
              const row: LoSamPosition[] = [];
              for (let x = 0; x < totalCols; x += 1) {
                row.push({
                  x,
                  y,
                  selected: false,
                  occupied: false, // Mặc định là trống
                  lotId: lotDetail.oneItem.loSam_ID || 0,
                });
              }
              positions.push(row);
            }
            setGridPositions(positions);

            // Load cây sâm data AFTER initializing grid
            await loadCaySams(lotDetail.oneItem.loSam_ID || 0);

            setSelectedPositions([]);
            setLotDetailLoaded(true);
          } else {
            console.error('Error loading lot detail:', lotDetail.message);
            // Fallback to basic lot info
            setSelectedLot(lot);
            const totalCols = lot.soCot + 1;
            const totalRows = lot.soHang;
            setGridSize({ x: totalCols, y: totalRows });
            setLotName(lot.tenLo);

            // Initialize empty grid
            const positions: LoSamPosition[][] = [];
            for (let y = 0; y < totalRows; y += 1) {
              const row: LoSamPosition[] = [];
              for (let x = 0; x < totalCols; x += 1) {
                row.push({
                  x,
                  y,
                  selected: false,
                  occupied: false,
                  lotId: lot.loSam_ID || 0,
                });
              }
              positions.push(row);
            }
            setGridPositions(positions);

            await loadCaySams(lot.loSam_ID || 0);
          }
        } catch (error) {
          console.error('Error loading lot detail:', error);
          // Fallback to basic lot info
          setSelectedLot(lot);
          const totalCols = lot.soCot + 1;
          const totalRows = lot.soHang;
          setGridSize({ x: totalCols, y: totalRows });
          setLotName(lot.tenLo);

          // Initialize empty grid
          const positions: LoSamPosition[][] = [];
          for (let y = 0; y < totalRows; y += 1) {
            const row: LoSamPosition[] = [];
            for (let x = 0; x < totalCols; x += 1) {
              row.push({
                x,
                y,
                selected: false,
                occupied: false,
                lotId: lot.loSam_ID || 0,
              });
            }
            positions.push(row);
          }
          setGridPositions(positions);

          await loadCaySams(lot.loSam_ID || 0);
        } finally {
          setLotDetailLoading(false);
        }
      } else {
        setGridSize({ x: 0, y: 0 });
        setLotName('');
        setGridPositions([]);
        setLotDetailLoading(false);
      }
    },
    [lots, loadCaySams]
  );

  // Update grid positions when caySams data is loaded
  useEffect(() => {
    if (caySams.length > 0 && gridPositions.length > 0) {
      setGridPositions((prev) => {
        const newPositions = prev.map((row) =>
          row.map((pos) => {
            // Skip walkway column
            const middleColumn = Math.floor(gridSize.x / 2);
            if (pos.x === middleColumn) {
              return pos; // Keep walkway as is
            }

            // Adjust x coordinate for positions after walkway
            let adjustedX = pos.x;
            if (pos.x > middleColumn) {
              adjustedX = pos.x - 1; // Shift back by 1 for positions after walkway
            }

            // Find cây sâm at this adjusted position
            const caySam = caySams.find((cs) => {
              const coords = parseViTriTrongLo(cs.viTriTrongLo);
              return coords ? coords.x === adjustedX && coords.y === pos.y : false;
            });

            return {
              ...pos,
              occupied: !!caySam,
              caySam: caySam || undefined,
            };
          })
        );
        return newPositions;
      });
    }
  }, [caySams, gridPositions.length, gridSize.x, parseViTriTrongLo]);

  // Test parsing function
  useEffect(() => {
    if (caySams.length > 0) {
      caySams.forEach((cs) => {
        const coords = parseViTriTrongLo(cs.viTriTrongLo);
        console.log(
          `${cs.viTriTrongLo} -> ${coords ? `x=${coords.x}, y=${coords.y}` : 'Failed to parse'}`
        );
      });
    }
  }, [caySams, parseViTriTrongLo]);

  const tracu = useTraCuuLoSam({ selectedLot, gridSize, gridPositions, loadCaySams });

  // Filter highlight criteria
  const [highlightStatus, setHighlightStatus] = useState<number>(0);
  const [highlightHealth, setHighlightHealth] = useState<number>(0);
  const [highlightAge, setHighlightAge] = useState<number>(0);
  const [filteredCount, setFilteredCount] = useState<number>(0);

  const { total, sucKhoe, camBien, getTotal, getSucKhoe, getCamBien } = useDashboardSam();
  useEffect(() => {
    getTotal();
    getSucKhoe();
    getCamBien();
    getOptionVuonTrong();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Get ginseng data from real cây sâm
  const getGinsengInfo = useCallback(
    (x: number, y: number) => {
      const middleColumn = Math.floor(gridSize.x / 2);
      if (x === middleColumn) return null; // Walkway

      const position = gridPositions[y]?.[x];

      if (position?.caySam) {
        const {caySam} = position;

        // Lấy nhật ký mới nhất (nếu có) để lấy điểm sức khỏe/tình trạng hiện tại
        let latestDiem: number | undefined;
        let latestTinhTrang: number | undefined;
        if (Array.isArray(caySam.caySamNhatKys) && caySam.caySamNhatKys.length > 0) {
          const list = caySam.caySamNhatKys.filter((n): n is any => !!n);
          if (list.length > 0) {
            const latest = list.reduce((acc: any, cur: any) => {
              if (!acc) return cur;
              return new Date(cur.ngayGhi).getTime() > new Date(acc.ngayGhi).getTime() ? cur : acc;
            }, null as any);
            latestDiem = latest?.diemSucKhoe;
            latestTinhTrang = latest?.tinhTrang;
          }
        }

        return {
          id: caySam.caySam_ID,
          name: `Cây sâm ${caySam.maCaySam || 'N/A'}`,
          description: `Cây sâm với mã ${caySam.maCaySam || 'N/A'}, vị trí ${caySam.viTriTrongLo}`,
          blockChain: caySam.blockChain,
          viTriTrongLo: caySam.viTriTrongLo,
          loSam_ID: caySam.loSam_ID,
          loSam: caySam.loSam,
          tuoiCay_ID: caySam.tuoiCay_ID,
          diemSucKhoe: latestDiem,
          tinhTrang: latestTinhTrang,
        };
      }
      return null;
    },
    [gridPositions, gridSize.x]
  );

  const handleCaySamUpdated = useCallback(() => {
    if (selectedLot?.loSam_ID) {
      reloadLotDetail(selectedLot.loSam_ID);
    }
  }, [selectedLot, reloadLotDetail]);

  // Handle deselecting ginsengs when filtering
  const handleDeselectGinseng = useCallback(
    (x: number, y: number) => {
      const key = `${x}-${y}`;
      setDeselectedGinsengs((prev) => {
        const newSet = new Set(prev);
        // TOGGLE: if already deselected, select back; otherwise deselect
        if (newSet.has(key)) {
          newSet.delete(key);
        } else {
          newSet.add(key);
        }

        // Kiểm tra xem có đủ cả 3 điều kiện không
        if (!highlightStatus || !highlightHealth || !highlightAge) {
          setFilteredCount(0);
          return newSet;
        }

        const middleColumn = Math.floor(gridSize.x / 2);
        let count = 0;
        for (let yy = 0; yy < gridPositions.length; yy += 1) {
          const row = gridPositions[yy] || [];
          for (let xx = 0; xx < row.length; xx += 1) {
            const positionKey = `${xx}-${yy}`;
            const info =
              xx !== middleColumn && !newSet.has(positionKey) ? getGinsengInfo(xx, yy) : null;
            if (info) {
              const ii = info as any;

              // Kiểm tra nếu cây đã có nhật ký trong tháng này và excludeLoggedThisMonth = true
              const hasLogThisMonth = (() => {
                const position = gridPositions[yy]?.[xx];
                if (!position?.caySam?.caySamNhatKys || position.caySam.caySamNhatKys.length === 0)
                  return false;
                const now = new Date();
                return position.caySam.caySamNhatKys.some((nk: any) => {
                  const d = new Date(nk?.ngayGhi as any);
                  return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
                });
              })();

              // Bỏ qua cây đã có nhật ký trong tháng này nếu excludeLoggedThisMonth = true
              if (!(excludeLoggedThisMonth && hasLogThisMonth)) {
                // Kiểm tra nếu có dữ liệu cần thiết để lọc
                // Tình trạng và điểm sức khỏe: cần có nhật ký (từ getGinsengInfo)
                // Tuổi cây: cần có thông tin cây sâm
                const hasRequiredData =
                  (!highlightStatus || (ii.tinhTrang !== null && ii.tinhTrang !== undefined)) &&
                  (!highlightHealth || (ii.diemSucKhoe !== null && ii.diemSucKhoe !== undefined)) &&
                  (!highlightAge || (ii.tuoiCay_ID !== null && ii.tuoiCay_ID !== undefined));

                const match =
                  hasRequiredData &&
                  (highlightStatus ? ii.tinhTrang === highlightStatus : true) &&
                  (highlightHealth ? ii.diemSucKhoe === highlightHealth : true) &&
                  (highlightAge ? Number(ii.tuoiCay_ID) === Number(highlightAge) : true);

                if (match) count += 1;
              }
            }
          }
        }
        setFilteredCount(count);
        return newSet;
      });
    },
    [
      gridPositions,
      gridSize.x,
      getGinsengInfo,
      highlightStatus,
      highlightHealth,
      highlightAge,
      excludeLoggedThisMonth,
    ]
  );

  /// Hàm handleLotChange cải tiến để hỗ trợ async/await
  const handleLotChangeWithResult = useCallback(
    async (lotId: number) => {
      const lot = lots.find((l) => l.loSam_ID === lotId);
      if (!lot) return null;

      setLotDetailLoading(true);
      try {
        const lotDetail = await losamApiService.getLoSam(lotId);
        if (lotDetail.messCode === 1 && lotDetail.oneItem) {
          const detailedLot = lotDetail.oneItem as LoSam;
          setSelectedLot(detailedLot);

          const totalCols = detailedLot.soCot + 1;
          const totalRows = detailedLot.soHang;
          setGridSize({ x: totalCols, y: totalRows });
          setLotName(detailedLot.tenLo);

          // Khởi tạo grid
          const positions: LoSamPosition[][] = [];
          for (let y = 0; y < totalRows; y += 1) {
            const row: LoSamPosition[] = [];
            for (let x = 0; x < totalCols; x += 1) {
              row.push({
                x,
                y,
                selected: false,
                occupied: false,
                lotId: detailedLot.loSam_ID || 0,
              });
            }
            positions.push(row);
          }
          setGridPositions(positions);

          // Load danh sách cây sâm thực tế
          const resCaySams = detailedLot.caySams || [];
          setCaySams(resCaySams);
          setLotDetailLoaded(true);
          return resCaySams; // Trả về để xử lý bước tiếp theo
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLotDetailLoading(false);
      }
      return null;
    },
    [lots]
  );

const handleFeaturedItemClick = useCallback(
  async (item: any) => {
    try {
      console.log('🚀 Điều hướng đến cây sâm:', item.id);

      // 1. Chọn vườn
      if (item.vuonTrong_ID) {
        const gardenId = Number(item.vuonTrong_ID); 
        const garden = gardens.find((g) => g.vuonTrong_ID === gardenId);
        if (garden) {
          setSelectedGarden(garden);
          // Load danh sách lô nhưng không cần đợi nó để chạy tiếp bước sau
          loadLots(gardenId); 
        }
      }

      // 2. Chọn lô và lấy dữ liệu cây sâm trực tiếp từ API
      let currentCaySams: CaySam[] = [];
      if (item.losam_ID) {
        const lotId = Number(item.losam_ID); 
        
        // handleLotChangeWithResult NÊN là hàm trả về data chi tiết của Lô đó
        const result = await handleLotChangeWithResult(lotId);
        
        if (result) {
          currentCaySams = result;
          // Vì handleLotChangeWithResult đã gọi setSelectedLot(detailedLot) bên trong,
          // chúng ta KHÔNG cần gọi lại setSelectedLot(fullLotData) ở đây nữa để tránh render thừa.
        }
      }

      // 3. Tìm tọa độ và mở Drawer (Tăng timeout lên một chút để Grid kịp render)
      // Sử dụng requestAnimationFrame hoặc timeout 500ms để đảm bảo DOM đã sẵn sàng
      setTimeout(() => {
        // Ưu tiên tìm trong danh sách vừa lấy từ API
        const target = currentCaySams.find((cs) => String(cs.caySam_ID) === String(item.id));
        
        // Lấy vị trí: ưu tiên từ target (API chi tiết), nếu không có dùng từ item (Carousel)
        const viTriStr = target?.viTriTrongLo || item.viTriTrongLo || '';
        const coords = parseViTriTrongLo(viTriStr);

        console.log('📍 Vị trí xác định:', viTriStr, coords);

        if (coords) {
          // Kích hoạt click vào đúng ô trên Grid
          tracu.handlePositionClick(coords.x, coords.y);
        } else {
          // Fallback nếu không parse được tọa độ
          tracu.setSelectedCellInfo({
            position: viTriStr || 'N/A',
            x: 0,
            y: 0,
            isEdit: true,
            caySam: target || (item as CaySam),
          });
          tracu.setOpenDrawer(true);
        }
      }, 1);  

    } catch (error) {
      console.error('❌ Lỗi điều hướng:', error);
    }
  },
  [gardens, loadLots, handleLotChangeWithResult, parseViTriTrongLo, tracu] 
  // Loại bỏ 'lots' khỏi dependency để tránh hàm bị tạo lại quá nhiều lần
);

  const renderLeftWidgets = (
    <>
      <Grid xs={12} paddingY={1} paddingX={0}>
        <AppWidgetSummary
          title="Tổng số cây sâm"
          total={total?.totalCaySam || 0}
          chart={{
            colors: [theme.palette.warning.light, theme.palette.warning.main],
            series: [8, 9, 31, 8, 16, 37, 8, 33, 46, 31],
          }}
        />
      </Grid>
      <Grid xs={12} paddingY={1} paddingX={0}>
        <AppWidgetSummary
          title="Tổng số cây yếu"
          total={total?.totalLoSam || 0}
          chart={{
            colors: [theme.palette.error.light, theme.palette.error.main],
            series: [20, 41, 63, 33, 28, 35, 50, 46, 11, 26],
          }}
        />
      </Grid>
    </>
  );

  const renderRightWidgets = (
    <Box display="flex">
      <Grid xs={6} md={6} p={0} pr={1}>
        <AppWidgetSummary
          title="Tổng số cây sâm"
          total={total?.totalCaySam || 0}
          chart={{
            colors: [theme.palette.warning.light, theme.palette.warning.main],
            series: [8, 9, 31, 8, 16, 37, 8, 33, 46, 31],
          }}
        />
      </Grid>

      <Grid xs={6} md={6} p={0} pl={1}>
        <AppWidgetSummary
          title="Tổng số cây yếu"
          total={total?.totalLoSam || 0}
          chart={{
            colors: [theme.palette.error.light, theme.palette.error.main],
            series: [20, 41, 63, 33, 28, 35, 50, 46, 11, 26],
          }}
        />
      </Grid>
    </Box>
  );
  const renderGridContent = useMemo(() => {
    // Dependency diemSucKhoeOptions được thêm vào dependency array
    if (gardensLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      );
    }
    if (!selectedGarden) {
      return (
        <>
          <Typography variant="h6" sx={{ mb: 2, px: { xs: 2, md: 0 } }}>
            Bản đồ lô sâm
          </Typography>
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">
              Vui lòng chọn vườn trồng để xem bản đồ lô sâm
            </Typography>
          </Box>
        </>
      );
    }
    if (!selectedLot && !lotName) {
      return (
        <>
          <Typography variant="h6" sx={{ mb: 2, px: { xs: 2, md: 0 } }}>
            Bản đồ lô sâm
          </Typography>
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">Vui lòng chọn lô sâm hoặc tạo lô mới</Typography>
          </Box>
        </>
      );
    }
    return (
      <LotGrid
        lotName={lotName}
        selectedLot={selectedLot}
        gridPositions={gridPositions}
        gridSize={gridSize}
        caySamsLoading={caySamsLoading}
        onCellClick={tracu.handlePositionClick}
        getGinsengInfo={getGinsengInfo}
        highlightStatus={highlightStatus}
        highlightHealth={highlightHealth}
        highlightAge={highlightAge}
        excludeLoggedThisMonth={excludeLoggedThisMonth}
        onDeselectGinseng={handleDeselectGinseng}
        deselectedGinsengs={deselectedGinsengs}
        onOpenDrawer={(position) => {
          tracu.handlePositionClick(position.x, position.y);
        }}
        loaiTuoiOptions={tracu.loaiTuoiOptions}
        diemSucKhoeOptions={diemSucKhoeOptions}
        onUpdateCaySam={() => {
          // Handle updating cay sam data
        }}
        onSaveCaySam={() => {
          // Reload data after save
          if (selectedLot?.loSam_ID) {
            reloadLotDetail(selectedLot.loSam_ID);
          }
        }}
        onDeleteCaySam={() => {
          // Handle deleting cay sam
          console.log('Delete cay sam');
          // Reload data after delete
          if (selectedLot?.loSam_ID) {
            reloadLotDetail(selectedLot.loSam_ID);
          }
        }}
      />
    );
  }, [
    gardensLoading,
    selectedGarden,
    selectedLot,
    lotName,
    gridPositions,
    gridSize,
    caySamsLoading,
    tracu,
    getGinsengInfo,
    highlightStatus,
    highlightHealth,
    highlightAge,
    excludeLoggedThisMonth,
    handleDeselectGinseng,
    deselectedGinsengs,
    diemSucKhoeOptions,
    reloadLotDetail,
  ]);

  return (
    <Container maxWidth={settings.themeStretch ? false : 'xl'} sx={{ padding: 0 }}>
      <CustomBreadcrumbs
        heading="Cây sâm của bạn"
        links={[{ name: 'Trang chủ', href: paths.dashboard.root }, { name: 'Cây sâm của bạn' }]}
        // action={
        //   <Button
        //     component={RouterLink}
        //     href={paths.dashboard.root}
        //     variant="contained"
        //     startIcon={<Iconify icon="eva:plus-fill" />}
        //   >
        //     Thêm lô sâm
        //   </Button>
        // }
      />

      <Grid container spacing={3}>
        <Grid xs={12} md={4}>
          <AppCurrentDownload
            title="Tình trạng sức khỏe sâm"
            subheader="Phân bố theo mức độ sức khỏe"
            chart={{
              colors: [
                theme.palette.success.darker,
                theme.palette.success.main,
                theme.palette.success.light,
                theme.palette.error.light,
                theme.palette.error.main,
              ],
              series: [
                { label: 'Rất tốt', value: sucKhoe?.totalRatTot || 0 },
                { label: 'Khá tốt', value: sucKhoe?.totalKhaTot || 0 },
                { label: 'Trung bình', value: sucKhoe?.totalTrungBinh || 0 },
                { label: 'Yếu', value: sucKhoe?.totalYeu || 0 },
                { label: 'Rất yếu', value: sucKhoe?.totalRatYeu || 0 },
              ],
            }}
            healthPercentage={sucKhoe?.healthPercentage || 0}
          />
          {!featuredLoading && featuredList.length > 0 && renderLeftWidgets}
        </Grid>

        <Grid container xs={12} md={8}>
          <Grid xs={12} md={12}>
            <AppSensorThermometer sensorDevices={camBien || []} />
          </Grid>
          <Grid xs={12} md={12}>
            {/* CỘT PHẢI: Logic hiển thị thay thế */}
            {/* eslint-disable-next-line no-nested-ternary, no-nested-ternary */}
            {featuredLoading ? (
              <Box sx={{ p: 5, textAlign: 'center' }}>
                <CircularProgress />
                <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                  Đang tải dữ liệu...
                </Typography>
              </Box>
            ) : featuredList.length > 0 ? (
              <AppFeatured list={featuredList} onItemClick={handleFeaturedItemClick} />
            ) : (
              renderRightWidgets
            )}
          </Grid>
        </Grid>

        <Grid xs={12} md={12} lg={12}>
          {/* // eslint-disable-next-line react/jsx-no-undef */}
          {/* Bên trái: Cấu hình lô sâm */}
          <Grid xs={12} md={3} marginBottom={2}>
            <Card sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Cấu hình lô sâm
              </Typography>

              <Stack spacing={3}>
                <FormControl fullWidth>
                  <InputLabel>Chọn vườn trồng</InputLabel>
                  <Select
                    value={selectedGarden?.vuonTrong_ID || ''}
                    label="Chọn vườn trồng"
                    onChange={(e) => handleGardenChange(Number(e.target.value))}
                    disabled={gardensLoading}
                  >
                    {optiongardens.map((garden) => (
                      <MenuItem key={garden.value || ''} value={garden.value || ''}>
                        {garden.text || ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel>Chọn lô sâm</InputLabel>
                  <Select
                    value={selectedLot?.loSam_ID || ''}
                    label="Chọn lô sâm"
                    onChange={(e) => handleLotChange(Number(e.target.value))}
                    disabled={!selectedGarden || lotsLoading}
                  >
                    {lotsLoading ? (
                      <MenuItem disabled>
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                        Đang tải...
                      </MenuItem>
                    ) : (
                      lots.map((lot) => (
                        <MenuItem key={lot.loSam_ID} value={lot.loSam_ID}>
                          {lot.tenLo} ({lot.soCot} x {lot.soHang})
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    label="Chiều rộng (X)"
                    type="number"
                    value={(() => {
                      if (selectedLot) return selectedLot.soCot;
                      if (gridSize.x > 0) return gridSize.x - 1;
                      return 0;
                    })()}
                    onChange={(e) =>
                      setGridSize((prev) => ({ ...prev, x: Number(e.target.value) + 1 }))
                    }
                    disabled
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="Chiều cao (Y)"
                    type="number"
                    value={gridSize.y}
                    onChange={(e) =>
                      setGridSize((prev) => ({ ...prev, y: Number(e.target.value) }))
                    }
                    disabled
                    sx={{ flex: 1 }}
                  />
                </Box>

                {selectedPositions.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Vị trí đã chọn: {selectedPositions.length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedPositions.map((pos) => `(${pos.x}, ${pos.y})`).join(', ')}
                    </Typography>
                  </Box>
                )}

                {selectedLot && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Thông tin lô sâm
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Tổng số cây sâm: {caySams.length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Diện tích: {selectedLot.dienTich} m²
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Card>

            {/* Camera Viewer - chỉ hiển thị khi đã load xong dữ liệu chi tiết */}
            {selectedLot && lotDetailLoaded && (
              <Card sx={{ p: 3, mt: 3 }}>
                <CameraViewer
                  loSamId={selectedLot.loSam_ID}
                  vuongSamId={selectedGarden?.vuonTrong_ID}
                />
              </Card>
            )}
          </Grid>

          {/* Bên phải: Filter + LotGrid + CaySamDrawer */}
          <Grid xs={12} md={9}>
            <Stack spacing={3}>
              {/* LotGrid ở giữa */}
              <Card sx={{ p: { xs: 0, md: 3 } }}>{renderGridContent}</Card>

              {/* CaySamDrawer ở phía dưới */}
              {tracu.openDrawer && (
                <Card sx={{ p: 3 }}>
                  <CaySamDrawer
                    open={tracu.openDrawer}
                    onClose={tracu.handleCloseDrawer}
                    title={tracu.selectedCellInfo?.isEdit ? 'Cập nhật cây sâm' : 'Thêm cây sâm mới'}
                    lotName={selectedLot?.tenLo || ''}
                    lotCode={selectedLot?.maLo || ''}
                    gardenName={selectedGarden?.tenVuon || ''}
                    positionLabel={tracu.selectedCellInfo?.position || ''}
                    caySamData={tracu.selectedCellInfo?.caySam || null}
                    onUpdateCaySam={(updatedData) => {
                      // Cập nhật caySamData trong selectedCellInfo
                      if (tracu.selectedCellInfo?.caySam) {
                        tracu.setSelectedCellInfo({
                          ...tracu.selectedCellInfo,
                          caySam: { ...tracu.selectedCellInfo.caySam, ...updatedData },
                        });
                      }
                    }}
                    loaiTuoiOptions={tracu.loaiTuoiOptions}
                    diemSucKhoeOptions={diemSucKhoeOptions}
                    loading={tracu.loading}
                    onRefresh={handleCaySamUpdated}
                    isEdit={!!tracu.selectedCellInfo?.isEdit}
                    onSave={() => {
                      if (selectedLot?.loSam_ID) {
                        reloadLotDetail(selectedLot.loSam_ID);
                      }
                    }}
                    onDelete={() => {
                      if (selectedLot?.loSam_ID) {
                        reloadLotDetail(selectedLot.loSam_ID);
                      }
                    }}
                  />
                </Card>
              )}
            </Stack>
          </Grid>
        </Grid>
      </Grid>
      {/* Update Growth Modal */}
      <UpdateCaySams
        open={updateGrowthOpen}
        onClose={() => setUpdateGrowthOpen(false)}
        loading={false}
        items={filteredCaySams}
        caySamById={(() => {
          const allow = new Set<string | number>(filteredCaySams.map((nk) => nk.caySamId || nk.id));
          const map: Record<string | number, any> = {};
          gridPositions.forEach((row) =>
            row.forEach((pos) => {
              const cs = pos.caySam;
              if (cs && allow.has(cs.caySam_ID)) map[cs.caySam_ID] = cs;
            })
          );
          return map;
        })()}
        filterState={{
          tinhTrang: highlightStatus || undefined,
          diem: highlightHealth || undefined,
        }}
        onConfirm={async (data) => {
          console.log('Update growth confirmed:', data);
          setUpdateGrowthOpen(false);

          // Reload lot detail và cập nhật grid positions
          if (selectedLot) {
            await reloadLotDetail(selectedLot.loSam_ID || 0);
          }

          // Reset filter state
          setHighlightStatus(0);
          setHighlightHealth(0);
          setHighlightAge(0);
          setDeselectedGinsengs(new Set());
          setFilteredCount(0);
        }}
      />
    </Container>
  );
}
